import {
  InfiniteData,
  useInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { gqlClient } from '@/api/graphql-client';
import {
  getNextPageParamFromPaginatedResult,
  getPreviousPageParamFromPaginatedResult,
} from '@/api/infiniteQueries';
import projectKeys from '@/api/queries/project/keyFactory';
import { graphql } from '@/gql';
import {
  GetFolderEntriesInput,
  GetProjectFolderEntriesQuery,
} from '@/gql/graphql';
import { PAGINATION_ARG_DEFAULTS } from '@payaca/shared-isomorphic/constants/graphql-pagination';
import { produce } from 'immer';
import { useMemo } from 'react';

const GET_PROJECT_FOLDER_ENTRIES = graphql(`
  query GetProjectFolderEntries(
    $projectId: ID!
    $input: GetFolderEntriesInput
    $pagination: PaginationInput!
  ) {
    projectById(id: $projectId) {
      id
      folderEntries(input: $input, pagination: $pagination) {
        items {
          ...BaseFolderEntry
        }
        limit
        offset
        totalCount
      }
    }
  }
`);

export type TFolderEntry = NonNullable<
  GetProjectFolderEntriesQuery['projectById']['folderEntries']
>['items'][number];

const useGetProjectFolderEntries = (
  projectId?: string,
  opts?: {
    input?: GetFolderEntriesInput;
    enabled?: boolean;
  }
) => {
  const input = opts?.input;

  const queryClient = useQueryClient();

  const queryFn = ({ pageParam }: { pageParam: unknown }) => {
    return gqlClient.request(GET_PROJECT_FOLDER_ENTRIES, {
      projectId: projectId!,
      input,
      pagination: {
        offset: pageParam as number,
        limit: PAGINATION_ARG_DEFAULTS.limit,
      },
    });
  };

  const { data, ...rest } = useInfiniteQuery({
    queryKey: projectKeys.folderEntries(projectId!, input),
    queryFn,
    initialPageParam: PAGINATION_ARG_DEFAULTS.offset,
    enabled: opts?.enabled ?? !!projectId,
    // If `input` contains a parentFolderId and we have already fetched all entries,
    // filter the cached data client-side before fetching it again from the server
    placeholderData: () => {
      if (
        !projectId ||
        !input ||
        !input.parentFolderId ||
        !input.parentFolderId.length
      ) {
        return;
      }

      const data = queryClient.getQueryData<
        InfiniteData<GetProjectFolderEntriesQuery, number>
      >(projectKeys.folderEntries(projectId));

      if (!data) {
        return;
      }

      return produce(data, (oldData) => {
        oldData.pages.forEach((page) => {
          if (!page.projectById.folderEntries) {
            return;
          }

          page.projectById.folderEntries.items =
            page.projectById.folderEntries?.items.filter(
              (entry) => entry.parentFolderId === input.parentFolderId
            );
        });
      });
    },
    getPreviousPageParam: getPreviousPageParamFromPaginatedResult(
      (firstPage) => firstPage.projectById.folderEntries
    ),
    getNextPageParam: getNextPageParamFromPaginatedResult(
      (lastPage) => lastPage.projectById.folderEntries
    ),
  });

  const projectFolderEntries = useMemo(() => {
    return data?.pages.flatMap(
      (page) => page.projectById.folderEntries?.items || []
    );
  }, [data]);

  const totalCount = useMemo(() => {
    return data?.pages[0].projectById.folderEntries?.totalCount ?? 0;
  }, [data]);

  return {
    data,
    projectFolderEntries,
    totalCount,
    ...rest,
  };
};

export default useGetProjectFolderEntries;
